import { FC, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import { strLengthValidation, urlValidation } from 'utils/patterns';
import { makeSelectOptions } from 'utils/makeSelectOptions';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { Button } from 'components/Core/Button';
import { Switch } from 'components/Core/Switch/Switch';
import { ControlledInput } from 'components/Inputs/ControlledInput';
import SelectComponent from 'components/Inputs/SelectComponent';
import { ICountryData } from 'stores/countries/types';
import { countriesOptions } from 'stores/countries/constants';
import { defaultValues } from './defaults';
import { countryFormTranslations } from './translations';
import { CountryFormTransFields } from './types';
import styles from './countryForm.module.scss';

export const CountryForm: FC = observer(() => {
  const {
    countriesStore: { countryData, editCountry, addCountry },
    mainStore: { getUser },
    usersStore: { setUsersCountryCodes },
    alertStore: { showAlert },
    popUpStore: { openPopUp, closePopUp },
  } = useStore();

  const { t } = useMyTranslations<CountryFormTransFields>([countryFormTranslations.name]);

  const navigate = useNavigate();

  const { mutate, isLoading } = useMutation(countryData ? editCountry : addCountry, {
    mutationKey: 'editCountry-addCountry',
    onSuccess: (res: ICountryData | null) => {
      showAlert(t(res ? 'createSuccess' : 'editSuccess'), 'success');
      closePopUp();
      // The backend may have changed the admins' country access: refresh the switcher in the
      // navbar (profile) and drop the cached list the admin-user popup picks countries from.
      getUser();
      setUsersCountryCodes(null);
      res && navigate(`/countries/${res.id}`);
    },
  });

  const useFormMethods = useForm<ICountryData>({
    defaultValues: countryData || defaultValues,
  });

  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { isSubmitting },
  } = useFormMethods;

  const onSubmitHandler = useCallback((inputData: ICountryData) => {
    openPopUp({
      title: t('confirmationTitle'),
      description: t('approve-text'),
      buttonLabel: t('confirm'),
      formName: 'confirmWithCode',
      cb: (verificationCode) =>
        mutate({
          data: {
            ...inputData,
            // Number inputs keep a string until blur; the backend expects numbers.
            capitalLatitude: Number(inputData.capitalLatitude),
            capitalLongitude: Number(inputData.capitalLongitude),
          },
          ...verificationCode,
        }),
    });
  }, []);

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <FormProvider {...(useFormMethods as any)}>
      <div className={styles.content}>
        <form className={styles.form}>
          <section className={`${styles.leftSection}`}>
            <div className={`${styles.formSection}`}>
              <ControlledInput name='defaultEngName' type='text' required label={t('defaultEngName')} />
              <SelectComponent
                name='code'
                label={t('code')}
                options={makeSelectOptions(countriesOptions)}
                maxMenuHeight={160}
                required
                isDisabled={!!countryData}
                onSelect={(val) => setValue('code', val.label)}
              />
              <ControlledInput name='domain' type='text' label={t('domain')} validation={urlValidation()} />
              <ControlledInput
                name='capitalName'
                type='text'
                label={t('capitalName')}
                validation={strLengthValidation(100)}
                errorText={t('capitalNameError')}
                required
              />
              <ControlledInput
                name='capitalLatitude'
                type='number'
                label={t('capitalLatitude')}
                min={-90}
                max={90}
                step='any'
                errorText={t('capitalLatitudeError')}
                required
              />
              <ControlledInput
                name='capitalLongitude'
                type='number'
                label={t('capitalLongitude')}
                min={-180}
                max={180}
                step='any'
                errorText={t('capitalLongitudeError')}
                required
              />
              <div className={styles.checkboxWrap}>
                <h6 className={`${styles.title}`}>{t('status')}</h6>
                <Switch
                  isActive={watch('status') === 'Active'}
                  onChange={() => setValue('status', getValues('status') === 'Active' ? 'Inactive' : 'Active')}
                />
              </div>
            </div>
            <div className={styles.bottomSection}>
              <Button
                title={t('save')}
                onClick={handleSubmit(onSubmitHandler)}
                isDisabled={isSubmitting || isLoading}
              />
            </div>
          </section>
        </form>
      </div>
    </FormProvider>
  );
});
